import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  AlertLiteComponent,
  ButtonModule,
  ChipsAutocompleteModule,
  DialogModule,
  LinkDirective,
} from '@lockton-apps/shared/ui/lui';
import { MatIconModule } from '@angular/material/icon';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { Contact, Quote, QUOTE_TYPES } from '@lockton-apps/shared/interfaces';
import { CobMetaPipe, MatchSorterPipe } from '@lockton-apps/shared/ui/pipes';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DifferenceByPipe } from '@lockton-apps/shared/util/ng-lodash';
import { compact, find, map } from 'lodash';

export interface AssignContactsDialogData {
  quote: Quote;
  contacts: Contact[];
}

export type AssignContactsDialogResult = {
  contacts: Contact[];
};

@Component({
    selector: 'la-assign-contact-dialog',
    imports: [
        CommonModule,
        DialogModule,
        ButtonModule,
        MatIconModule,
        LinkDirective,
        MatDialogModule,
        CobMetaPipe,
        AlertLiteComponent,
        ChipsAutocompleteModule,
        MatFormFieldModule,
        MatChipsModule,
        MatAutocompleteModule,
        MatInputModule,
        MatchSorterPipe,
        ReactiveFormsModule,
        DifferenceByPipe,
    ],
    template: `
    <lui-dialog-container>
      <lui-dialog-header>
        <div class="mat-h2">Assign a contact(s)</div>

        <button
          luiDialogClose
          luiIconButton
          size="xl"
          aria-label="Close"
          i18n-aria-label
        >
          <mat-icon svgIcon="close_light"></mat-icon>
        </button>
      </lui-dialog-header>
      <lui-dialog-content>
        <div class="mw-md mx-auto">
          <p i18n
            >Please select the contact(s) you would like to assign to
            {{ (quote.classOfBusinessId | cobMeta).title }}
            {{
              quote.type === this.quoteTypes.Renewal ? 'renewal' : 'quote'
            }}.</p
          >

          <lui-alert-lite [compact]="true" level="elevated"
            >Please note the contact(s) you choose here will receive all email
            communications throughout the renewal journey.
          </lui-alert-lite>

          <mat-form-field class="mt-3">
            <mat-label i18n>Contacts</mat-label>

            <mat-chip-grid #cobChipGrid [formControl]="control" class="flex-1">
              <mat-chip-row
                *ngFor="let option of control.value"
                [value]="option"
              >
                {{ displayWith(option) }}
                <button matChipRemove>
                  <mat-icon svgIcon="close"></mat-icon>
                </button>
              </mat-chip-row>
              <input
                id="cob-input"
                type="text"
                matInput
                luiChipsAutocomplete
                [matChipInputFor]="cobChipGrid"
                #cobInput="matInput"
                placeholder="Enter contact name or email"
                autocomplete="off"
                [matAutocomplete]="cobAutocomplete"
                i18n-placeholder
              />
            </mat-chip-grid>

            <mat-autocomplete #cobAutocomplete displayWithPath="'label'">
              <mat-option
                *ngFor="
                  let option of contacts
                    | differenceBy : control.value : 'userId'
                    | matchSorter
                      : cobInput.value
                      : { keys: ['displayName', 'email'] }
                "
                [value]="option"
              >
                <div class="d-flex flex-column">
                  <span>
                    {{ option.displayName }}
                  </span>
                  <span class="text-grayLight"> {{ option.email }} </span>
                </div>
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>
        </div>
      </lui-dialog-content>
      <lui-dialog-actions align="between">
        <button luiLink matDialogClose>Cancel</button>
        <button luiButton (click)="submit()">Assign Contact(s)</button>
      </lui-dialog-actions>
    </lui-dialog-container>
  `
})
export class AssignContactsDialogComponent {
  readonly quote!: Quote;
  readonly contacts!: Contact[];
  readonly quoteTypes = QUOTE_TYPES;

  control = new FormControl<Contact[]>([], { nonNullable: true });

  constructor(
    private dialogRef: MatDialogRef<
      AssignContactsDialogComponent,
      AssignContactsDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) data: AssignContactsDialogData
  ) {
    if (!data.contacts || !data.quote) {
      throw new Error('Missing data for AssignContactsDialogComponent');
    }
    this.quote = data.quote;
    this.contacts = data.contacts.filter((contact) => contact.enabled);

    const contactsValue = compact(
      map(data.quote.assignedContactUsers, (c) =>
        find(data.contacts, { userId: c.userId, enabled: true })
      )
    );

    this.control.setValue(contactsValue);
  }

  submit() {
    this.dialogRef.close({ contacts: this.control.value });
  }

  displayWith(contact: Contact) {
    return contact.displayName;
  }
}
